import { capitalize } from "lodash";
import { CurrencyConfigRankedRes, IWatchListDataRes } from "src/store/features/api/generated";
import { IWatchlistData } from "./defaults";
import { styled } from "@mui/styles";
import IconMapping from "src/utils/iconMapping";

export const GreenPlus = styled("span")({
  color: "#056645",
  backgroundColor: "#F7FFFC",
});

export const RedMinus = styled("span")({
  color: "#790202",
  backgroundColor: "#FFFBFB",
});

function analyzeNumber(number: number) {
  if (!number) return { isZeroOrLess: true, decimalPlaces: 0 };
  // Convert the number to a string
  const numberString = number.toString();

  // Check if the number is zero or less than zero
  const isZeroOrLess = number <= 1;

  // Check the number of decimal places
  const decimalPlaces = numberString.split(".")[1]?.length || 0;

  return {
    isZeroOrLess,
    decimalPlaces,
  };
}

/**
 * Converts a number to a string with a specific number of decimal places.
 */
export const handleToFixedAmount = (value: number, decimalPlaces = 2): string => {
  if (value === 0) return "0";

  return (Math.floor(value * 100) / 100).toFixed(decimalPlaces);
};

/**
 * Formats a balance amount into a string representation with currency symbol and code.
 *
 * @param balance - The balance amount to be formatted.
 * @param currencyCode - The currency code to use for formatting (e.g., "AUD").
 * @returns A formatted string representing the balance with currency symbol and code (e.g., "$100,000 AUD").
 *
 * @example
 * balance = 100000000;
 * const formattedBalance = formatBalance(balance, "AUD"); // Returns "$100,000,000 AUD"
 */
export const formatBalance = (
  balance: number,
  currencyCode: string,
  includeCode: boolean = true,
): string => {
  let formatter: Intl.NumberFormat;

  formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currencyCode ?? "USD",
    maximumFractionDigits: 0,
  });
  const formattedBalance = formatter.format(balance);
  let currencySymbol;
  // if balance is less than zero, slice the currency symbol from the formatted balance and NOT the minus sign
  if (balance < 0) {
    currencySymbol = formattedBalance.substring(1, 1);
  } else {
    currencySymbol = formattedBalance.substring(0, 1);
  }
  if (includeCode) {
    return formattedBalance.replace(currencySymbol, "") + " " + currencyCode;
  }
  return formattedBalance.replace(currencySymbol, "");
};

/**
 * Formats a number into a string representation with a specific number of decimal places.
 *
 * @param {string} currencyCode - The currency code to use for formatting (e.g., "AUD").
 * @returns {string} A string representing the currency symbol (e.g., "$").
 */
export const getCurrencySymbol = (currencyCode: string): string => {
  // Create a formatter for the specified currency
  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currencyCode,
    minimumFractionDigits: 0,
  });

  // Format a zero value to get just the currency formatting
  const formatted = formatter.format(0);

  // Extract the symbol - it's the first non-digit, non-space, non-minus character
  const symbol = formatted.replace(/[-\d\s,.]/g, "")[0] || "";

  // for aud, return A$ to clearly distinguish
  if (symbol === "A") return symbol + "$";

  return symbol;
};

/**
 * Formats a change percentage into a string representation.
 *
 * @param change - The change percentage to be formatted.
 * @returns A formatted string representing the change percentage (e.g., "2.64%").
 *
 * @example
 * const change = 2.6454;
 * const formattedChange = formatChange(change); // Returns "2.64%"
 */
export const formatChange = (change: number): string => {
  const formatter = new Intl.NumberFormat("en-US", {
    style: "percent",
    maximumFractionDigits: 2,
  });

  // if change is greater than 0, add a "+" sign
  if (change / 100 > 0) {
    return `${formatter.format(change / 100)}`;
  }
  // else return the formatted change which will include the '-' sign
  return formatter.format(change / 100);
};

/**
 * Formats a dollar change amount based on a percentage change.
 *
 * @param balance - The balance amount to calculate the change from.
 * @param percent - The percentage change to apply to the balance.
 * @returns A formatted string representing the dollar change amount (e.g., "$2,645.40").
 *
 * @example
 * const balance = 10000;
 * const percent = 2.6454;
 * const formattedDollarChange = formatDollarChange(balance, percent); // Returns "$26.454"
 */
export const formatDollarChange = (dollarChange: number): string => {
  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  });
  return `${formatter.format(dollarChange).toLocaleString()}`;
};

export const removeCurvedBrackets = (str: string) => {
  return str.replace(/\([^()]*\)/g, "").trim();
};

function removeCharacterPrefix(inputString: string): string {
  // Define an array of characters to remove
  const charactersToRemove = ["A", "CA", "HK", "JP", "NZ", "US"];

  // Create a regular expression pattern to match any of the characters to remove
  const pattern = new RegExp(charactersToRemove.join("|"), "g");

  // Use the replace method with the regular expression pattern to remove the characters
  const result = inputString.replace(pattern, "");

  return result;
}

/**
 * Formats a numerical value as a currency string with context-aware decimal places.
 *
 * For standard positive numbers:
 * - Values under $100 are formatted with 2 decimal places (e.g., $99.99)
 * - Values $100 and above are formatted with no decimal places (e.g., $150)
 *
 * For zero or negative numbers:
 * - If the number has 6 or more decimal places, it shows 6 decimals
 * - Otherwise shows no decimal places
 *
 * @param value - The numerical value to format
 * @param currencyCode - The ISO 4217 currency code (e.g., "USD", "EUR")
 * @param decimal - Default number of decimal places (currently unused, kept for backwards compatibility)
 * @returns A formatted currency string using the specified currency code and appropriate decimal places
 *
 * @example
 * formatPrice(99.99, "USD")     // Returns "$99.99"
 * formatPrice(150, "AUD")       // Returns "A$150"
 * formatPrice(-45.50, "USD")    // Returns "-$45.50"
 * formatPrice(0.000001, "USD")  // Returns "$0.000001"
 */
export const formatPrice = (value: number, currencyCode: string, decimal: number = 2): string => {
  const { isZeroOrLess, decimalPlaces } = analyzeNumber(value);
  let formatter: Intl.NumberFormat;

  const formatDecimals = (decimalPlaces: number) => {
    return decimalPlaces >= 6 ? 6 : decimalPlaces;
  };

  // Determine decimal places based on value
  const getDecimalPlaces = (value: number) => {
    return Math.abs(value) < 100 ? 2 : 0;
  };

  formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currencyCode ?? "USD",
    currencyDisplay: "symbol",
    maximumFractionDigits: isZeroOrLess ? formatDecimals(decimalPlaces) : getDecimalPlaces(value),
  });

  return formatter.format(value);
};

export const formatNumbersWithCommas = (value: string | number, decimal: number = 2): string => {
  const newValue = Number(value);
  const { isZeroOrLess, decimalPlaces } = analyzeNumber(newValue);
  let formatter: Intl.NumberFormat;

  if (isZeroOrLess) {
    formatter = new Intl.NumberFormat(undefined, {
      maximumFractionDigits: decimalPlaces >= 6 ? 6 : 0,
    });
  } else {
    formatter = new Intl.NumberFormat(undefined, {
      maximumFractionDigits: decimal,
    });
  }

  return formatter.format(newValue);
};

/**
 * Formats a market capitalization value into a string representation with currency symbol and notation.
 *
 * @param value - The market capitalization value to be formatted.
 * @returns A formatted string representing the market capitalization with currency symbol and notation (e.g., "A$60k").
 *
 * @example
 * const marketCapValue = 60000;
 * const formattedMarketCap = formatMarketCap(marketCapValue, "EUR"); // Returns "€60k"
 */
export const formatMarketCap = (value: number, currencyCode: string): string => {
  const formatter = new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: currencyCode ?? "USD",
    currencyDisplay: "symbol", // This ensures the symbol is used
    notation: "compact",
    compactDisplay: "short",
    maximumFractionDigits: 0,
  });
  return formatter.format(value).replace("K", "k");
};

/**
 * Truncates a string if it exceeds a maximum length, appending an ellipsis (...) at the end.
 *
 * @param str - The input string to be truncated.
 * @param maxLength - The maximum length of the string before truncation.
 * @returns A truncated string if it exceeds the maximum length, otherwise the original string.
 *
 * @example
 * const longString = "Bank of America Sees No Crypto Winter Given User Adoption, Despite Volatile Market Conditions";
 * const maxLength = 50;
 * const truncatedString = truncateString(longString, maxLength); // Returns "Bank of America Sees No Crypto Winter Given ..."
 */
export const truncateString = (str: string, maxLength: number): string => {
  if (str.length > maxLength) {
    return str.substring(0, maxLength - 3) + "......";
  }
  return str;
};

/**
 * Formats a transaction type string into a more user-friendly representation.
 *
 * @param type - The transaction type string to be formatted.
 * @returns A formatted string representing the transaction type.
 *
 * @example
 * const transactionType = "Crypto Deposit";
 * const formattedTransactionType = formatTransactionType(transactionType); // Returns "Deposit"
 */
export const formatTransactionType = (type: string): string => {
  switch (type) {
    case "Crypto Deposit":
    case "Fiat Deposit":
      return "Deposit";
    case "Crypto Withdrawal":
    case "Fiat Withdrawal":
      return "Withdrawal";
    default:
      return capitalize(type);
  }
};

// Define a generic type for the coins
type ICoin = CurrencyConfigRankedRes | IWatchListDataRes;

/**
 * Formats data from the backend for the Watchlist component.
 *
 * @param coins - An array of coins to be formatted.
 * @param code - The currency code to use for formatting (default: "AUD").
 * @param isCoinInWatchlist - Optional function to check if a coin is in the watchlist. If not provided, defaults to true (for watchlist items).
 * @returns An array of formatted data for the Watchlist component.
 *
 * @example
 * const coins = [{ name: "Bitcoin", logoUrl: "https://example.com/bitcoin.png", price: 45000, change1d: 5.4, market_cap: 800000000000 }];
 * const formattedWatchlistData = formatWatchlistData(coins, "USD"); // Returns an array of formatted data for the Watchlist component
 */
export const formatWatchlistData = <T extends ICoin>(
  coins: T[],
  code: string,
  isCoinInWatchlist?: (code: string) => boolean,
): IWatchlistData[] => {
  if (!coins) return [];

  return coins.map((coin): IWatchlistData => {
    // Determine coin code for IconMapping fallback
    let coinCode = "";
    if ("code" in coin) {
      coinCode = coin.code.toLowerCase();
    } else {
      coinCode = coin.currencyConfig.code.toLowerCase();
    }

    // Determine the actual coin code for watchlist checking
    let actualCoinCode = "";
    if ("code" in coin) {
      actualCoinCode = coin.code;
    } else {
      actualCoinCode = coin.currencyConfig.code;
    }

    // Check if coin is in watchlist if function is provided, otherwise default to true (for watchlist items)
    const isFavourite = isCoinInWatchlist ? isCoinInWatchlist(actualCoinCode) : true; // Default to true when formatting watchlist items (no check function provided)

    const data = {
      name: removeCurvedBrackets(coin.name),
      code: "",
      currencyConfig: {},
      // Use logoUrl if available, otherwise use IconMapping as fallback
      icon: coin.logoUrl || IconMapping(coinCode, 24),
      rawPrice: Number(coin.price),
      price: formatPrice(Number(coin.price), code),
      sparkline: coin.sparkline ?? [],
      change: <></>,
      marketCap: formatMarketCap(Number(coin.market_cap), code),
      favourite: isFavourite,
      alert: false,
    };

    if ("code" in coin) {
      data.code = coin.code.toLowerCase();
      data.currencyConfig = coin;
    } else {
      data.code = coin.currencyConfig.code.toLowerCase();
      data.currencyConfig = coin.currencyConfig;
    }

    let formattedChange = <></>;
    if (coin.change1d && coin.change1d > 0) {
      formattedChange = (
        <>
          <GreenPlus data-testid="#yourcrypto-positive-percent-change">
            +{formatChange(coin.change1d ?? 0).replace(/^[-+]/, "")}
          </GreenPlus>
        </>
      );
    } else if (coin.change1d && coin.change1d < 0) {
      formattedChange = (
        <>
          <RedMinus data-testid="#yourcrypto-negative-percent-change">
            -{formatChange(coin.change1d ?? 0).replace(/^[-+]/, "")}
          </RedMinus>
        </>
      );
    } else {
      <>{formatChange(Number(coin.change1d))}</>;
    }

    data.change = formattedChange;

    return data;
  });
};

export function formattedDateAndTime(timestamp: number | undefined): string {
  if (!timestamp) return "";
  const months = [
    "jan",
    "feb",
    "mar",
    "apr",
    "may",
    "jun",
    "jul",
    "aug",
    "sep",
    "oct",
    "nov",
    "dec",
  ];

  // Parse input string into a Date object
  const date = new Date(timestamp);

  // Extract hours and minutes
  // const hours = String(date.getUTCHours()).padStart(2, "0");
  // const minutes = String(date.getUTCMinutes()).padStart(2, "0");

  // Extract day, month, and year
  const day = date.getUTCDate();
  const month = months[date.getUTCMonth()];
  const year = date.getUTCFullYear();

  // Construct formatted string
  const formattedString = `${day} ${month} ${year}`;

  return formattedString;
}

export function formatChartPrice(price: number, code: string) {
  if (Number.isNaN(price)) return "";
  const { isZeroOrLess } = analyzeNumber(price);
  let formatter: Intl.NumberFormat;

  if (isZeroOrLess) {
    formatter = new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: code ?? "USD",
      currencyDisplay: "symbol", // This ensures the symbol is used
      notation: "compact",
      compactDisplay: "short",
      maximumFractionDigits: 6,
    });
  } else {
    formatter = new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: code ?? "USD",
      currencyDisplay: "symbol", // This ensures the symbol is used
      notation: "standard",
      compactDisplay: "short",
      maximumFractionDigits: 0,
    });
  }
  return removeCharacterPrefix(formatter.format(price));
}

export function isToday(dateString: string) {
  const today = new Date();
  const givenDate = new Date(dateString);

  // Check if the given date has the same year, month, and day as today
  return (
    today.getFullYear() === givenDate.getFullYear() &&
    today.getMonth() === givenDate.getMonth() &&
    today.getDate() === givenDate.getDate()
  );
}
